import { atom } from 'jotai';
import {
  AboutLead,
  ContactInfo,
  LeadDetail,
  LeadVisuals,
  MappedLead,
  MaterialItem,
  PersonalInfo,
  Product,
  Project,
  ProjectDetails,
  PropertyDetail,
  PropertyVisuals,
  RawLead,
  RawUserInfo,
  RWLink,
} from '../@types/lead';
import { formatAddressline1 } from '../utils';
import { DetailLabels, DetailRecord, SectionTitle } from '../@types/general';
import { LEAD_DETAIL_LABELS, SECTION_TITLES, PROPERTY_DETAIL_LABELS } from '../constants/labels';

// todo: fix this type to be either leadDetail Items or Materials???
export interface LeadSections {
  sectionTitle: SectionTitle['text'];
  leadDetailItems?: Array<DetailRecord>;
  materials?: Array<MaterialItem>;
}

export interface PropertySections {
  sectionTitle: SectionTitle['text'];
  propertyDetailItems?: Array<DetailRecord>;
}

export interface PersonalInfoSections {
  sectionTitle: SectionTitle['text'];
  personalInfoDetailItems?: Array<DetailRecord>;
}

export class Lead implements MappedLead {
  leadDetail: LeadDetail;

  leadVisuals: LeadVisuals;

  sectionTitles: Array<SectionTitle>;

  leadLabels: DetailLabels<Omit<LeadDetail, 'materialList'>>;

  projects: Array<Project>;

  propertyDetail: PropertyDetail;

  propertyVisuals: PropertyVisuals;

  propertyLabels: DetailLabels<PropertyDetail>;

  personalInfo: PersonalInfo;

  personalInfoLabels: Record<string, string>;

  constructor(
    rawLead?: RawLead,
    sectionTitles?: Array<SectionTitle>,
    leadDetailLabels?: DetailLabels<LeadDetail>,
    propertyLabels?: DetailLabels<PropertyDetail>,
    personalInfoLabels?: Record<string, string>
  ) {
    this.leadDetail = {
      contactInfo: Lead.getMappedContactInfo(rawLead?.userInfo),
      aboutLead: Lead.getMappedAboutLead(rawLead),
      // TODO: move projectDetails to be contained within a current project object instead to support multiple projects per lead?
      projectDetails: Lead.getMappedFirstProjectDetails(rawLead),
      materialList: rawLead?.projects[0]?.materialList ?? [],
    };

    this.leadVisuals = {
      initialProjectImage: rawLead?.projects[0]?.projectInfo?.photoOfHouse ?? '',
      finalProjectImage: rawLead?.projects[0]?.projectInfo?.photoOfLastDesign ?? '',
      appliedProducts: rawLead?.projects[0]?.projectInfo?.productApplied ?? [],
    };

    this.projects = Lead.getProjects(rawLead);

    this.sectionTitles = sectionTitles ?? SECTION_TITLES; // TODO: use Locale system

    this.leadLabels = leadDetailLabels ?? LEAD_DETAIL_LABELS; // TODO: use Locale system

    this.propertyDetail = {
      property: rawLead?.projects[0]?.propertyInfo?.property ?? {
        roofArea: '-',
        roofSquares: '-',
        roofToGround: '-',
        roofSegmentCount: '-',
        roofPredominatePitch: {
          largestSegmentPitch: '-',
          weightedAveragePitch: '-',
        },
      },
      location: rawLead?.projects[0]?.propertyInfo?.location ?? {
        lat: '-',
        long: '-',
      },
    };

    this.propertyVisuals = {
      propertyImageUrl: rawLead?.projects[0]?.propertyInfo?.images?.ortho ?? '',
    };

    this.propertyLabels = propertyLabels ?? PROPERTY_DETAIL_LABELS;

    this.personalInfo = rawLead?.userInfo?.personalInfo ?? {
      age: '-',
      gender: '-',
      housePrice: '-',
      AVGHousePrice: '-',
      householdIncome: '-',
      creditCardStatus: '-',
      lastHouseSaleDate: '-',
      loanAbilityStatus: '-',
      AVGHouseholdIncome: '-',
    };

    this.personalInfoLabels = personalInfoLabels ?? {
      age: 'Age',
      gender: 'Gender',
      housePrice: 'House Price',
      AVGHousePrice: 'Avg House Price',
      householdIncome: 'Household Income',
      creditCardStatus: 'Credit Card Status',
      lastHouseSaleDate: 'Last House Sale Date',
      loanAbilityStatus: 'Loan Ability Status',
      AVGHouseholdIncome: 'Avg Household Income',
    };
  }

  private static getMappedContactInfo(rawUserInfo?: RawUserInfo): ContactInfo {
    return {
      leadName: `${rawUserInfo?.firstName ?? ''} ${rawUserInfo?.lastName ?? ''}`, // TODO: new function to concat lead name from firt and last
      phone: rawUserInfo?.phone ?? '',
      email: rawUserInfo?.email ?? '',
      addressLine1: rawUserInfo?.address ? formatAddressline1(rawUserInfo?.address) : '',
      addressLine2: rawUserInfo?.address?.address2 ?? '',
      zip: rawUserInfo?.zip ?? '',
    };
  }

  private static getMappedAboutLead(rawLead?: RawLead): AboutLead {
    return {
      leadLabel: rawLead?.scienceInfo?.leadLabel ?? '',
      predictiveCloseRate: rawLead?.scienceInfo?.predictiveCloseRate ?? '',
      source: (rawLead?.siteName || rawLead?.siteIdentifier) ?? '',
      entryPoint: rawLead?.userInfo?.formSource ?? '',
      lastActivity: rawLead?.digitalInfo?.visitDate ?? null,
    };
  }

  private static getMappedFirstProjectDetails(rawLead?: RawLead): ProjectDetails {
    return {
      timeSpent: rawLead?.digitalInfo?.timeOnSite ?? '',
      designsMade: rawLead?.userInfo?.totalProjects ?? 0,
      totalProductsApplied: rawLead?.digitalInfo?.productApplies ?? 0,
      numberOfColorsApplied: rawLead?.digitalInfo?.colorApplies ?? 0,
      numberOfPageViews: rawLead?.digitalInfo?.pageViews ?? '',
    };
  }

  private static getProjects(rawLead?: RawLead): Array<Project> {
    return rawLead?.projects?.length
      ? rawLead?.projects
      : [
        {
          projectInfo: {
            projectId: 'default',
            projectCreated: null,
            photoOfHouse: 'images/project-before-placeholder.png',
            photoOfLastDesign: 'images/project-after-placeholder.png',
            productApplied: [] as Array<Product>,
          },
          links: [] as Array<RWLink>,
          materialList: [] as Array<MaterialItem>,
        },
      ];
  }

  getSectionTitleByName(propertyName: string): string {
    const title = this.sectionTitles.find(t => t.name === propertyName) ?? '';

    return title ? title.text : 'MISSING TITLE';
  }

  // TODO: fix typing on reduce
  private getLeadSectionDetailList(
    propertyName: keyof LeadDetail | keyof DetailLabels<LeadDetail>
  ): Array<DetailRecord> {
    const labels = propertyName !== 'materialList' && this.leadLabels[propertyName];
    const values = this.leadDetail[propertyName];
    const list = Object.entries(values) as Array<[keyof LeadDetail, LeadDetail[keyof LeadDetail]]>;
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const result = list.reduce((acc: any, [key, value]: any) => {
      if (key !== 'leadLabel') {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        acc.push({ label: labels[key], value });
      }

      return acc;
    }, []);

    return result;
  }

  private getPropertySectionDetailList(
    propertyName: keyof PropertyDetail | keyof DetailLabels<PropertyDetail>
  ): Array<DetailRecord> {
    const labels = this.propertyLabels[propertyName];
    const values = this.propertyDetail[propertyName];

    const list = Object.entries(values || {}) as Array<
      [keyof PropertyDetail, PropertyDetail[keyof PropertyDetail]]
    >;
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const result = list.reduce((acc: any, [key, value]: any) => {
      // Handle nested objects (like roofPredominatePitch)
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        Object.entries(value).forEach(([nestedKey, nestedValue]) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const nestedLabel = labels[key]?.[nestedKey] || nestedKey;
          if (nestedValue !== undefined && nestedValue !== null && nestedValue !== '') {
            acc.push({ label: nestedLabel, value: String(nestedValue) });
          } else {
            acc.push({ label: nestedLabel, value: '-' });
          }
        });
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const label = labels[key];
        if (value !== undefined && value !== null && value !== '') {
          acc.push({ label, value: String(value) });
        } else {
          acc.push({ label, value: '-' });
        }
      }

      return acc;
    }, []);

    return result;
  }

  // TODO: fix typing on reduce
  getLeadDetailAsList(): Array<LeadSections> {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const detailList = Object.entries(this.leadDetail).reduce((acc: any, curr: any) => {
      const detail = {
        sectionTitle: this.getSectionTitleByName(curr[0]),
        leadDetailItems: curr[0] !== 'materialList' ? this.getLeadSectionDetailList(curr[0]) : null,
        materials: curr[0] === 'materialList' ? curr[1] : null,
      };
      const result = [...acc, detail];

      return result;
    }, []);

    return detailList;
  }

  getPropertyDetailAsList(): Array<PropertySections> {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const detailList = Object.entries(this.propertyDetail).reduce((acc: any, curr: any) => {
      const detail = {
        sectionTitle: this.getSectionTitleByName(curr[0]),
        propertyDetailItems: this.getPropertySectionDetailList(curr[0]),
      };
      const result = [...acc, detail];

      return result;
    }, []);

    return detailList;
  }

  private getPersonalInfoSectionDetailList(fields: string[]): Array<DetailRecord> {
    const labels = this.personalInfoLabels;
    const values = this.personalInfo;

    return fields.reduce((acc: Array<DetailRecord>, field) => {
      const value = values[field];
      const label = labels[field] || field;

      if (value !== undefined && value !== null && value !== '-') {
        acc.push({ label, value: String(value) });
      } else {
        acc.push({ label, value: '-' });
      }
      return acc;
    }, []);
  }

  getPersonalInfoDetailAsList(): Array<PersonalInfoSections> {
    return [
      {
        sectionTitle: 'Demographic',
        personalInfoDetailItems: this.getPersonalInfoSectionDetailList([
          'age',
          'gender',
          'householdIncome',
          'housePrice'
        ]),
      },
      {
        sectionTitle: 'Financial Traits',
        personalInfoDetailItems: this.getPersonalInfoSectionDetailList([
          'creditCardStatus',
          'loanAbilityStatus'
        ]),
      },
      {
        sectionTitle: 'Neighborhood/Cohort',
        personalInfoDetailItems: this.getPersonalInfoSectionDetailList([
          'AVGHouseholdIncome',
          'AVGHousePrice',
          'lastHouseSaleDate'
        ]),
      },
    ];
  }
}

export const leadAtom = atom<Lead>(new Lead());

export const leadIdAtom = atom<string>('');
